import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import { UsuarioCadastro, UsuarioAgendamento, UsuarioAuth, DataTablePagination, Medico, OperadoraBackoffice } from '@/types/_'
import { ConfigUsuario } from '@/types/core'
import _ from 'lodash'
import Apis from '@/Apis/_'
import { Especialidade } from '@/types/interfaces/Especialidade'
import { MedicoLocalAtendimento, PessoaAgendamentoSuporte } from '@/types/interfaces/Usuario'
import rolesMock from '@/utils/roles-mock'

/** Campo pelo qual a busca de pessoa é feita. Um por vez — o backend rejeita mais de um (FXCS-6701). */
export type TipoBuscaPessoa = 'nome' | 'email' | 'cpf'

/** Campo da busca da tela Pessoas (Agendamento): além de Usuários, também CRM e HisId (FXCS-6701). */
export type TipoBuscaPessoaAgendamento = TipoBuscaPessoa | 'crm' | 'hisId'

/** Tamanho mínimo do termo de nome e e-mail, espelhando o validator do backend. */
export const TAMANHO_MINIMO_BUSCA_PESSOA = 3

/** A busca por CPF é por igualdade: só o CPF completo encontra alguém. */
export const TAMANHO_CPF = 11

@Module({ namespaced: true, name: 'ConfigUsuarioStore' })
export default class ConfigUsuarioStore extends VuexModule {
  private configUsuarioImp: ConfigUsuario.IConfigUsuario = new ConfigUsuario.ConfigUsuario()
  private usuariosDefault: UsuarioCadastro[] = []
  private pessoasAgendamentoDefault: PessoaAgendamentoSuporte[] = []

  private medicoLocalAtendimentoDefault: MedicoLocalAtendimento = {
    locaisAtendimento: [],
    locaisAtendimentoMedico: [],
    medicoId: '',
  }

  private usuarioAgendamentoDefault: UsuarioAgendamento = {
    habilitarMedico: false,
    voltaStatus: false,
    hospitais: [],
    dadosUsuarioOperadora: {
      idsOperadoras: [],
    },
    medico: {
      cbosId: [],
      crm: '',
      crmUf: '',
      medicosLocaisAtendimento: [],
    },
    dadosPessoa: {
      telefone: '',
      id: '',
      cpf: '',
      email: '',
      nome: '',
    },
    dadosUsuarioSeguranca: {
      login: '',
      permissoesExtras: [],
      legacyPageAuthorized: [],
      legacyRoles: [],
      legacyProfile: '',
      defaultPage: {
        KeyAuthorized: '',
        view: '',
      },
    },
    tipoUsuarioAgendamento: '',
    hospitalLocalAtendimento: [],
  }

  private usuarioAuthDefault: UsuarioAuth = {
    email: '',
    name: '',
    id: '',
    role: '',
  }

  private pessoaAgendamentoDefault: PessoaAgendamentoSuporte = {
    id: '',
    nome: '',
    cpf: '',
    conselhoTipo: 'CRM',
    conselhoNumero: '',
    conselhoUf: '',
    email: '',
    telefone: '',
    temLogin: false,
    pessoaEhMedico: false,
    medicoHospitais: [],
  }

  public usuarioAgendamento: UsuarioAgendamento = { ...this.usuarioAgendamentoDefault }
  public usuarioAuth: UsuarioAuth = { ...this.usuarioAuthDefault }
  public usuarios: UsuarioCadastro[] = [...this.usuariosDefault]
  public pessoasAgendamento: PessoaAgendamentoSuporte[] = [...this.pessoasAgendamentoDefault]
  public pessoaAgendamento: PessoaAgendamentoSuporte = { ...this.pessoaAgendamentoDefault }
  public operadoras: OperadoraBackoffice[] = []
  public medicos: Medico[] = []

  public legacyProfiles: any[] = ['UsuarioBackoffice', 'UsuarioBackOfficeSomenteLeitura']
  public permissoes: any[] = []
  public legacyRoles: any[] = rolesMock

  public tiposContato: any[] = [
    {
      id: 'Email',
      text: 'Email',
    },
    {
      id: 'Celular',
      text: 'Celular',
    },
    {
      id: 'Telefone',
      text: 'Telefone',
    },
  ]

  public tiposPessoaContato: any[] = [
    {
      id: 'Padrao',
      text: 'Padrão',
    },
    {
      id: 'Preferencia',
      text: 'Preferência',
    },
  ]

  public configUsuariosTableHeader: any[] = [
    { text: 'Nome', class: 'table-headers', align: 'left', value: 'nome', sortable: false },
    { text: 'CPF', class: 'table-headers', align: 'left', value: 'cpf', sortable: false },
    { text: 'E-mail', class: 'table-headers', align: 'left', value: 'email', sortable: false },
    { text: 'Data cadastro', class: 'table-headers', align: 'left', value: 'dataCadastro', sortable: false },
    { text: 'Data exclusão', class: 'table-headers', align: 'left', value: 'dataExclusao', sortable: false },
    { text: 'Ações', class: 'table-headers', value: '', align: 'center', sortable: false },
  ]

  public modal: any = {
    createUser: false,
    editUser: false,
  }

  public cbosIds: Especialidade[] = []

  private paginationDefault: DataTablePagination = {
    descending: false,
    page: 1,
    rowsPerPage: 10,
    sortBy: null,
    totalItems: 0,
    rowsPerPageItems: [10, 50, 100, 200],
  }

  public pagination: DataTablePagination = { ...this.paginationDefault }

  public searchPessoa: string = ''

  /** Campo escolhido no seletor da busca: 'nome' | 'email' | 'cpf' (FXCS-6701). */
  public tipoBuscaPessoa: TipoBuscaPessoa = 'nome'

  /** Campo escolhido no seletor da tela Pessoas (FXCS-6701). */
  public tipoBuscaPessoaAgendamento: TipoBuscaPessoaAgendamento = 'nome'

  /**
   * A busca só acontece quando o usuário clica em Pesquisar. Enquanto for false,
   * a tela não consulta o backend — nem na abertura (FXCS-6701).
   */
  public buscaPessoaRealizada: boolean = false

  @Mutation
  setPessoaId(id: string) {
    this.usuarioAgendamento.dadosPessoa.id = id
  }

  @Mutation
  setSearchPessoa(val: string) {
    this.searchPessoa = val
  }

  @Mutation
  setTipoBuscaPessoa(val: TipoBuscaPessoa) {
    this.tipoBuscaPessoa = val
  }

  @Mutation
  setTipoBuscaPessoaAgendamento(val: TipoBuscaPessoaAgendamento) {
    this.tipoBuscaPessoaAgendamento = val
  }

  @Mutation
  setBuscaPessoaRealizada(val: boolean) {
    this.buscaPessoaRealizada = val
  }

  @Mutation
  setUsuarioAuth(usuario: UsuarioAuth) {
    this.usuarioAuth = usuario
  }

  @Mutation
  setUsuarioAgendamento(usuario: UsuarioAgendamento) {
    this.usuarioAgendamento = { ...this.usuarioAgendamento, ...usuario }
    if (usuario.hospitais) {
      this.usuarioAgendamento.hospitais = usuario.hospitais.map(c => _.toUpper(c))
    }

    if (usuario.dadosUsuarioSeguranca) {
      this.usuarioAgendamento.dadosUsuarioSeguranca.legacyPageAuthorized = usuario.dadosUsuarioSeguranca.legacyPageAuthorized
      this.usuarioAgendamento.dadosUsuarioSeguranca.legacyRoles = usuario.dadosUsuarioSeguranca.legacyRoles
      this.usuarioAgendamento.dadosUsuarioSeguranca.permissoesExtras = usuario.dadosUsuarioSeguranca.permissoesExtras
      this.usuarioAgendamento.dadosUsuarioSeguranca.defaultPage = usuario.dadosUsuarioSeguranca.defaultPage
      this.usuarioAgendamento.dadosUsuarioSeguranca.legacyProfile = usuario.dadosUsuarioSeguranca.legacyProfile
    }
  }

  @Mutation
  removeHospital(value: string) {
    const index = this.usuarioAgendamento.hospitais.indexOf(value)
    if (index >= 0) this.usuarioAgendamento.hospitais.splice(index, 1)
  }

  @Mutation
  removeOperadora(value: string) {
    const index = this.usuarioAgendamento.dadosUsuarioOperadora.idsOperadoras.indexOf(value)
    if (index >= 0) this.usuarioAgendamento.dadosUsuarioOperadora.idsOperadoras.splice(index, 1)
  }

  @Mutation
  removePermission(value: string) {
    const index = this.usuarioAgendamento.dadosUsuarioSeguranca.permissoesExtras.indexOf(value)
    if (index >= 0) this.usuarioAgendamento.dadosUsuarioSeguranca.permissoesExtras.splice(index, 1)
  }

  @Mutation
  removeLegacyPageAuthorized(value: string) {
    const index = this.usuarioAgendamento.dadosUsuarioSeguranca.legacyPageAuthorized.indexOf(value)
    if (index >= 0) this.usuarioAgendamento.dadosUsuarioSeguranca.legacyPageAuthorized.splice(index, 1)
  }

  @Mutation
  removeLegacyRoles(value: string) {
    const index = this.usuarioAgendamento.dadosUsuarioSeguranca.legacyRoles.indexOf(value)
    if (index >= 0) this.usuarioAgendamento.dadosUsuarioSeguranca.legacyRoles.splice(index, 1)
  }

  @Mutation
  addLegacyRoles(value: string) {
    const index = this.usuarioAgendamento.dadosUsuarioSeguranca.legacyRoles.indexOf(value)
    if (index < 0) this.usuarioAgendamento.dadosUsuarioSeguranca.legacyRoles.push(value)
  }

  @Mutation
  clearCamposMedico() {
    this.usuarioAgendamento.medico.crm = ''
    this.usuarioAgendamento.medico.crmUf = ''
    this.usuarioAgendamento.medico.cbosId = []
  }

  @Mutation
  clearPerfilChange() {
    this.usuarioAgendamento.hospitais = []
    this.usuarioAgendamento.medico.medicosLocaisAtendimento = [{ ...this.medicoLocalAtendimentoDefault }]
    this.medicos.map((e) => {
      const t: any = e
      t.disabled = false
      return t
    })
  }

  @Mutation
  setCbosIds(values: Especialidade[]) {
    this.cbosIds = values
  }

  @Mutation
  setTodasPermissoes(values: any[]) {
    this.permissoes = values
  }

  @Mutation
  setDataNascimento(value: string) {
    this.usuarioAgendamento.dadosPessoa.dataNascimento = value
  }

  @Mutation
  removeEspecialidade(value: any) {
    const index = this.usuarioAgendamento.medico.cbosId.indexOf(value)
    if (index >= 0) this.usuarioAgendamento.medico.cbosId.splice(index, 1)
  }

  @Mutation
  addMedicoLocalAtendimento() {
    this.usuarioAgendamento.medico.medicosLocaisAtendimento.push({ ...this.medicoLocalAtendimentoDefault })
  }

  @Mutation
  updateMedico() {
    this.usuarioAgendamento.medico.medicosLocaisAtendimento.map((c) => {
      this.medicos.map((e) => {
        const t = e
        if (c.medicoId === t.id) {
          t.disabled = true
        }
        return t
      })
      return c
    })

    this.medicos.map((m) => {
      const temp = m
      if (!this.usuarioAgendamento.medico.medicosLocaisAtendimento.some(c => c.medicoId === m.id)) {
        temp.disabled = false
      }
      return temp
    })
  }

  @Mutation
  clearLocalAtendimento(medicoId: string) {
    this.usuarioAgendamento.medico.medicosLocaisAtendimento.map((c) => {
      const temp = c
      if (temp.medicoId === medicoId) {
        temp.locaisAtendimento = []
      }
      return temp
    })
  }

  @Mutation
  removeMedicoLocalAtendimento(index: number) {
    this.usuarioAgendamento.medico.medicosLocaisAtendimento.splice(index, 1)
  }

  @Mutation
  clearUsuarioAgendamento() {
    this.usuarioAgendamento = { ...this.usuarioAgendamentoDefault }
    this.usuarioAgendamento.dadosPessoa.id = ''
  }

  @Mutation
  clearUsuarioAuth() {
    this.usuarioAuth = { ...this.usuarioAuthDefault }
  }

  @Mutation
  setModal(modal: any) {
    this.modal[modal.name] = modal.value
  }

  @Mutation
  setOperadoras(value: OperadoraBackoffice[]) {
    this.operadoras = value
  }

  @Mutation
  setUsuarios(values: UsuarioCadastro[]) {
    this.usuarios = values
  }

  @Mutation
  setPessoasAgendamento(value: PessoaAgendamentoSuporte[]) {
    this.pessoasAgendamento = value
  }

  @Mutation
  clearPessoasAgendamento() {
    this.pessoasAgendamento = [...this.pessoasAgendamentoDefault]
  }

  @Mutation
  setPessoaAgendamento(value: PessoaAgendamentoSuporte) {
    this.pessoaAgendamento = { ...this.pessoaAgendamentoDefault, ...value }
  }

  @Mutation
  clearPessoaAgendamento() {
    this.pessoaAgendamento = { ...this.pessoaAgendamentoDefault }
  }

  @Mutation
  setPagination(val: DataTablePagination) {
    this.pagination = val
  }

  @Mutation
  clearPagination() {
    this.pagination = { ...this.paginationDefault }
  }

  @Mutation
  setMedicos(medicos: Medico[]) {
    this.medicos = medicos
  }

  @Mutation
  setLocaisAtendimentoMedico(params: any) {
    this.usuarioAgendamento.medico.medicosLocaisAtendimento.map((c) => {
      const temp: MedicoLocalAtendimento = c
      if (temp.medicoId === params.medicoId) {
        temp.locaisAtendimentoMedico = params.locaisAtendimento
      }
      return temp
    })
  }

  @Mutation
  clearUsuarios() {
    this.usuarios = [...this.usuariosDefault]
  }

  @Action
  async getAllLocalAtendimentoByMedicoId(id: string) {
    const request = await this.configUsuarioImp.getAllLocalAtendimentoByMedicoId(id)
    if (request.success) this.context.commit('setLocaisAtendimentoMedico', { medicoId: id, locaisAtendimento: request.data })
    return request
  }

  @Action
  async getPessoalAll(dataTableItems: DataTablePagination) {
    const termo = (this.searchPessoa || '').trim()

    // Nada de buscar sem o usuário pedir (FXCS-6701). Cobre a abertura da tela e
    // os refresh de grade após criar/editar/excluir usuário, que antes cairiam
    // na listagem completa. Guard aqui e não em cada chamador: são 6.
    const tamanhoMinimo = this.tipoBuscaPessoa === 'cpf'
      ? TAMANHO_CPF
      : TAMANHO_MINIMO_BUSCA_PESSOA

    const tamanhoDoTermo = this.tipoBuscaPessoa === 'cpf'
      ? termo.replace(/\D/g, '').length
      : termo.length

    if (!this.buscaPessoaRealizada || tamanhoDoTermo < tamanhoMinimo) {
      return { success: true, data: { pessoas: [], total: 0 } }
    }

    // Um filtro nomeado por vez: o backend devolve 400 se vier mais de um (FXCS-6701).
    const params: any = {
      page: dataTableItems.page || this.paginationDefault.page,
      rowsPerPage: dataTableItems.rowsPerPage || this.paginationDefault.rowsPerPage,
      [this.tipoBuscaPessoa]: termo,
    }

    const request = await this.configUsuarioImp.getPessoalAll(params)
    if (request.success) {
      this.context.commit('setUsuarios', request.data.pessoas)
      this.context.commit('setPagination', {
        descending: false,
        page: dataTableItems.page || this.paginationDefault.page,
        rowsPerPage: dataTableItems.rowsPerPage || this.paginationDefault.rowsPerPage,
        sortBy: null,
        totalItems: request.data.total,
        rowsPerPageItems: this.paginationDefault.rowsPerPageItems,
      })
    }
    return request
  }

  @Action
  async getAllPessoaAgendamento(dataTableItems: DataTablePagination) {
    const params: any = {
      pagina: dataTableItems.page || this.paginationDefault.page,
      qtdPorPagina: dataTableItems.rowsPerPage || this.paginationDefault.rowsPerPage,
      campo: this.tipoBuscaPessoaAgendamento,
      termo: (this.searchPessoa || '').trim(),
    }
    const request = await this.configUsuarioImp.getAllPessoaAgendamento(params)
    if (request.success) {
      this.context.commit('setPessoasAgendamento', request.data.pessoas as PessoaAgendamentoSuporte[] || [])
      this.context.commit('setPagination', {
        descending: false,
        page: dataTableItems.page || this.paginationDefault.page,
        rowsPerPage: dataTableItems.rowsPerPage || this.paginationDefault.rowsPerPage,
        sortBy: null,
        totalItems: request.data.total,
        rowsPerPageItems: this.paginationDefault.rowsPerPageItems,
      })
    }
    return request
  }

  @Action
  async getPessoaAgendamentoById(id: string) {
    try {
      const request = await this.configUsuarioImp.getPessoaAgendamentoById(id)
      if (request.success) this.context.commit('setPessoaAgendamento', request.data as PessoaAgendamentoSuporte)
      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  async getPessoaById(id: string) {
    try {
      const request = await this.configUsuarioImp.getUsuarioAgendamentoById(id)
      if (request.success) {
        this.context.commit('setUsuarioAgendamento', request.data)
      }
      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  async getPermissoes() {
    const request = await this.configUsuarioImp.getPermissoes()
    if (request.success) {
      const permissoes = request.data.map(p => p.nome).sort((a, b) => a.localeCompare(b))
      this.context.commit('setTodasPermissoes', permissoes)
    }
    return request
  }

  @Action
  async deletePessoa(id: string) {
    try {
      const request = await this.configUsuarioImp.deletePessoa(id)
      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  async usuarioAgendamentoUpsert() {
    const params = { usuario: { ...this.usuarioAgendamento } }
    // @ts-ignore
    params.usuario = _.pickBy(params.usuario, v => v !== '')
    // @ts-ignore
    params.usuario.backoffice = _.pickBy(params.usuario.backoffice, v => v !== '')
    // @ts-ignore
    params.usuario.pessoa = _.pickBy(params.usuario.dadosPessoa, v => v !== '')

    const bodyRequest = {
      usuario: {
        dadosPessoa: _.pickBy(params.usuario.dadosPessoa, v => v !== ''),
        // @ts-ignore
        voltaStatus: params.usuario.backoffice.voltaStatus || params.usuario.voltaStatus,
        hospitais: params.usuario.hospitais,
        dadosUsuarioOperadora: params.usuario.dadosUsuarioOperadora || { idsOperadoras: [] },
        dadosUsuarioSeguranca: params.usuario.dadosUsuarioSeguranca,
        tipoUsuarioAgendamento: params.usuario.tipoUsuarioAgendamento,
      },
      hospitalLocalAtendimento: params.usuario.hospitalLocalAtendimento || [],
    }

    const request = await this.configUsuarioImp.usuarioAgendamentoUpsert(bodyRequest)

    return request
  }

  @Action
  async atualizaPessoaAgendamento() {
    const payload = { ...this.pessoaAgendamento }

    if (!this.pessoaAgendamento.pessoaEhMedico) {
      delete payload.conselhoNumero
      delete payload.conselhoTipo
      delete payload.conselhoUf
    }

    const request = await this.configUsuarioImp.atualizaPessoaAgendamento(payload)
    return request
  }

  @Action
  async getAllOperadoras() {
    try {
      const request = await Apis.Agendamento.SolicitacaoCirurgiaApi.getAllOperadoras()
      if (request.success) this.context.commit('setOperadoras', request.data.operadoras as OperadoraBackoffice[] || [])
      return request
    } catch (error) {
      this.context.commit('setOperadoras', [] as OperadoraBackoffice[])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }
}
